/**
 * F45 (Wave 2D) — engineering-authored compare-to-previous delta indicator for
 * the analytics KPI cards + funnel-step cards.
 *
 * `Stat.delta` is a static `ReactNode` + a `deltaTone` prop; there is no
 * computed percent-change primitive in @bravemove/ui-web at MVP (the F44 PercentDelta
 * is finance-namespaced). This composition renders an `AnalyticsKpiCard.deltaPercent`
 * value with an up/down arrow + a positive/negative/neutral tone.
 *
 * TODO(F45 design-polish): promote ActivityHeatmap + PercentDelta to
 * @bravemove/ui-web primitives in the sprint-end design-polish wave (meta-plan 2E).
 */
import { UpIcon, ChevronDownIcon } from '@bravemove/design-tokens/icons';
import { deltaTone, roundPercent } from '../analyticsHelpers';

export interface PercentDeltaProps {
  /** Signed percent change (e.g. 11.1 or -4.2). */
  readonly deltaPercent: number;
  /** Suffix label, e.g. "vs previous". */
  readonly label?: string;
}

export function PercentDelta({ deltaPercent, label }: PercentDeltaProps) {
  const tone = deltaTone(deltaPercent);
  // Render at 1-decimal precision — the backend emits raw float deltas.
  const abs = roundPercent(Math.abs(deltaPercent));
  return (
    <span
      className="inline-flex items-center gap-1 tabular-nums"
      data-testid="analytics-percent-delta"
      data-tone={tone}
    >
      {tone === 'positive' ? (
        <UpIcon size={11} color="currentColor" aria-hidden="true" />
      ) : tone === 'negative' ? (
        <ChevronDownIcon size={11} color="currentColor" aria-hidden="true" />
      ) : null}
      <span>
        {deltaPercent > 0 ? '+' : deltaPercent < 0 ? '−' : ''}
        {abs}%
      </span>
      {label ? <span className="text-ink-3 font-normal">{label}</span> : null}
    </span>
  );
}
